import { z } from 'zod';
import { EncryptedArtifactRef } from './artifact-ref.js';
import { FileDiff, Id } from './common.js';
/**
 * Openable references (openable-refs): paths, commits, branches and local preview URLs that an
 * agent mentions in a transcript are resolved by the run's machine (the trusted endpoint) and
 * opened in the viewer's inspector. Requests and replies travel as encrypted commands
 * (`run.resolve_refs`, `run.read_ref`); large bodies travel as encrypted artifacts. The hub never
 * sees a path or a byte of content.
 */
export const REF_MAX_CANDIDATES = 100;
/** Inline reply bodies (base64 or text) above this travel as an encrypted artifact instead. */
export const REF_INLINE_MAX_BYTES = 192 * 1024;
export const REF_MATCH_LIMIT = 20;
const Text = z.string().min(1).max(4096);
const Line = z.number().int().positive().max(10_000_000);
/** What the viewer detected; `id` is the client's cache key, echoed back. */
export const RefCandidate = z.object({
    id: z.string().min(1).max(512),
    kind: z.enum(['path', 'commit', 'branch', 'url']),
    text: Text,
    line: Line.optional(),
    lineEnd: Line.optional(),
});
// agent-sharing: `assets` = the project's approved asset folders; paths start with the folder name.
export const RefRoot = z.enum(['worktree', 'main', 'assets']);
/** A resolved, openable target (paths are repo-relative, `/`-separated). */
export const RefTarget = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('file'),
        path: z.string(),
        root: RefRoot,
        size: z.number().int().nonnegative(),
        mime: z.string(),
        lines: z.number().int().nonnegative().optional(),
        line: Line.optional(),
        lineEnd: Line.optional(),
    }),
    z.object({
        type: z.literal('dir'),
        path: z.string(),
        root: RefRoot,
        entries: z.number().int().nonnegative(),
    }),
    z.object({
        type: z.literal('commit'),
        sha: z.string().regex(/^[0-9a-f]{40}$/),
        subject: z.string(),
    }),
    z.object({ type: z.literal('branch'), name: z.string(), sha: z.string() }),
    /** A loopback/LAN URL whose path maps to static files in the worktree (snapshot-able). */
    z.object({
        type: z.literal('preview'),
        url: z.string(),
        path: z.string(),
        root: RefRoot,
    }),
]);
export const RefResolution = z.object({
    id: z.string(),
    status: z.enum(['resolved', 'ambiguous', 'missing', 'denied']),
    target: RefTarget.optional(),
    /** Ambiguous partial paths: the candidates to choose from (capped). */
    matches: z.array(RefTarget).max(REF_MATCH_LIMIT).optional(),
    /** `missing` URL on a live-only dev server; `denied` secret/escape (no detail). */
    reason: z.enum(['live-only', 'outside', 'secret', 'not-found']).optional(),
});
export const ResolveRefsResult = z.object({
    /** Worktree HEAD the answers are cached against. */
    head: z.string().nullable(),
    results: z.array(RefResolution),
});
const RelPath = z.string().max(4096);
const Rev = z
    .string()
    .max(255)
    .regex(/^[A-Za-z0-9._/~^-]+$/)
    .refine((r) => !r.startsWith('-') && !r.includes('..'), 'bad revision');
/** What to read: file (optionally at a revision), directory, commit, file diff vs base, preview. */
export const RefRead = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('file'),
        path: RelPath,
        root: RefRoot.optional(),
        rev: Rev.optional(),
    }),
    z.object({ type: z.literal('dir'), path: RelPath, root: RefRoot.optional() }),
    z.object({ type: z.literal('commit'), sha: Rev }),
    z.object({ type: z.literal('diff'), path: RelPath }),
    z.object({ type: z.literal('preview'), url: z.string().max(4096) }),
]);
/** Body transport: inline text/base64 up to REF_INLINE_MAX_BYTES, else an encrypted artifact. */
export const RefBody = z.union([
    z.object({ encoding: z.literal('utf8'), text: z.string() }),
    z.object({ encoding: z.literal('base64'), data: z.string() }),
    z.object({ encoding: z.literal('artifact'), artifact: EncryptedArtifactRef }),
]);
export const RefDirEntry = z.object({
    name: z.string(),
    type: z.enum(['file', 'dir']),
    size: z.number().int().nonnegative().optional(),
});
export const ReadRefResult = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('file'),
        path: z.string(),
        root: RefRoot,
        rev: z.string().optional(),
        mime: z.string(),
        size: z.number().int().nonnegative(),
        text: z.boolean(),
        lines: z.number().int().nonnegative().optional(),
        body: RefBody.optional(),
        /** Rendered first page (PDF) or downscaled image when the original is not sent inline. */
        preview: RefBody.optional(),
        previewMime: z.string().optional(),
        /** Text files were scrubbed of detected secrets before leaving the machine. */
        scrubbed: z.boolean().optional(),
        tooLarge: z.boolean().optional(),
    }),
    z.object({
        type: z.literal('dir'),
        path: z.string(),
        root: RefRoot,
        entries: z.array(RefDirEntry),
        truncated: z.boolean().optional(),
    }),
    z.object({
        type: z.literal('commit'),
        sha: z.string(),
        author: z.string(),
        date: z.number(),
        message: z.string(),
        files: z.array(FileDiff),
        truncated: z.boolean().optional(),
    }),
    z.object({
        type: z.literal('diff'),
        path: z.string(),
        base: z.string(),
        files: z.array(FileDiff),
    }),
    z.object({
        type: z.literal('preview'),
        url: z.string(),
        path: z.string(),
        capturedAt: z.number(),
        /** Self-contained HTML (assets inlined, scripts removed) for a sandboxed frame. */
        body: RefBody,
        assets: z.number().int().nonnegative(),
        omitted: z.number().int().nonnegative(),
    }),
]);
export const REF_COMMAND_PAYLOADS = {
    /** Resolve detected references against the run's worktree (then the main checkout). */
    'run.resolve_refs': z.object({
        runId: Id,
        refs: z.array(RefCandidate).min(1).max(REF_MAX_CANDIDATES),
    }),
    /** Read one resolved reference's content (scoped, capped, scrubbed). */
    'run.read_ref': z.object({ runId: Id, target: RefRead }),
};
//# sourceMappingURL=refs.js.map